import React, {
	createContext,
	useContext,
	useMemo,
	useState,
	useCallback,
	useEffect
} from 'react';
import PropTypes from 'prop-types';
import { usePrefs, useLabels } from 'wsm-common-data';
import { useSelector } from 'react-redux';
import useGalleryModal from '../hooks/useGalleryModal';
import usePubSubListeners from '../hooks/usePubSubListeners';
import useOpenGalleryParam from '../hooks/useOpenGalleryParam';
import useImageTitle from '../hooks/useImageTitle';
import useMedia from '../hooks/useMedia';
import usePanoramicImage from '../hooks/usePanoramicImage';

const GalleryContext = createContext(null);
GalleryContext.displayName = 'GalleryContext';

export const useGalleryContext = () => {
	const context = useContext(GalleryContext);
	if (!context) {
		throw new Error(
			'useGalleryContext must be used within a GalleryProvider'
		);
	}
	return context;
};

export const GalleryProvider = ({ children, overrides }) => {
	const {
		openModal,
		setOpenModal,
		carouselIndex,
		setCarouselIndex,
		activeTab,
		setActiveTab,
		primarySliderRef,
		onOpenModal,
		onClose
	} = useGalleryModal();

	const prefs = usePrefs();
	const labels = useLabels();
	const isDesktop = useMedia('(min-width: 576px)', true);

	const galleryToolbarPlacement = prefs.galleryToolbarPlacement || 'top';
	const isBlackBackgroundModal = prefs.enableBlackBackgroundModal === 'true';
	const toolbarIconStyle =
		prefs.toolbarIconStyle === 'round' ? 'round' : 'default';
	const toolbarIconColor =
		prefs.toolbarIconColor === 'light' ? 'light' : 'dark';
	const show360Image = prefs.show360Image === 'true';

	// `allImages` is the unfiltered feed. It is kept intact so that index
	// restoration (e.g. WIAPI updates) can still match against every image.
	const { imagesToDisplay: allImages = [] } = useSelector(
		(storeState) => storeState.media
	);

	const panoramicImage = usePanoramicImage(allImages, show360Image);

	// The panoramic image is always the last image of the feed and is shown
	// in its own 360 modal, so it is excluded from the gallery, the
	// thumbnails and the photo count.
	const imagesToDisplay = useMemo(() => {
		if (!panoramicImage) {
			return allImages;
		}
		const lastIndex = allImages.length - 1;
		return allImages[lastIndex]?.src === panoramicImage.src
			? allImages.slice(0, lastIndex)
			: allImages;
	}, [allImages, panoramicImage]);

	const {
		vehicle = {},
		pathParameters,
		accountId
	} = useSelector((storeState) => storeState.widgetInfo);

	const imgTitle = useImageTitle({ vehicle, labels });

	let totalImages = imagesToDisplay.length;
	if (
		totalImages === 1 &&
		imagesToDisplay[0]?.src?.includes('unavailable_stockphoto')
	) {
		totalImages = 0;
	}

	const firstThumbnail =
		totalImages > 0 ? imagesToDisplay[0]?.thumbnail : null;

	const [openPanoramicModal, setOpenPanoramicModal] = useState(false);
	const onOpenPanoramicModal = useCallback(
		() => setOpenPanoramicModal(true),
		[]
	);
	const onClosePanoramicModal = useCallback(
		() => setOpenPanoramicModal(false),
		[]
	);

	// Detection is asynchronous, so the carousel can already sit on an index
	// that no longer exists once the panoramic image is filtered out.
	useEffect(() => {
		const lastIndex = imagesToDisplay.length - 1;
		if (carouselIndex > lastIndex) {
			setCarouselIndex(lastIndex > 0 ? lastIndex : 0);
		}
	}, [imagesToDisplay.length, carouselIndex, setCarouselIndex]);

	usePubSubListeners({
		onClose,
		openModal,
		totalImages,
		firstThumbnail,
		isBlackBackgroundModal
	});

	useOpenGalleryParam({ onOpenModal, setCarouselIndex, setActiveTab });

	const state = {
		openModal,
		carouselIndex,
		activeTab,
		totalImages,
		imagesToDisplay,
		allImages,
		panoramicImage,
		openPanoramicModal
	};

	const actions = {
		setOpenModal,
		setCarouselIndex,
		setActiveTab,
		onOpenModal,
		onClose,
		onOpenPanoramicModal,
		onClosePanoramicModal
	};

	const meta = {
		primarySliderRef: overrides?.primarySliderRef || primarySliderRef,
		isDesktop,
		galleryToolbarPlacement,
		imgTitle,
		vehicle,
		prefs,
		labels,
		isBlackBackgroundModal,
		toolbarIconStyle,
		toolbarIconColor,
		pathParameters,
		accountId,
		...overrides
	};

	const value = useMemo(
		() => ({ state, actions, meta }),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			openModal,
			carouselIndex,
			activeTab,
			totalImages,
			isDesktop,
			galleryToolbarPlacement,
			imgTitle,
			toolbarIconStyle,
			toolbarIconColor,
			imagesToDisplay,
			panoramicImage,
			openPanoramicModal
		]
	);

	return (
		<GalleryContext.Provider value={value}>
			{children}
		</GalleryContext.Provider>
	);
};

GalleryProvider.propTypes = {
	children: PropTypes.node.isRequired,
	overrides: PropTypes.shape({
		primarySliderRef: PropTypes.shape({})
	})
};

GalleryProvider.defaultProps = {
	overrides: null
};

export default GalleryContext;
